import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DirectoryRow } from './DirectoryRow';
import { ArtifactCard } from './ArtifactCard';
import { ImageCard } from './ImageCard';
import type { ArtifactLayout, DirectoryItem, DirectoryKind, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface SortableDirectoryItemProps {
  item: DirectoryItem;
  kind: DirectoryKind;
  /** Artifacts only: card (the rail) or row (the list). Defaults to the rail. */
  artifactLayout?: ArtifactLayout;
  selected?: boolean;
  menuOpen?: boolean;
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** Artifacts only: what plays over the thumbnail while it is being written. See `ArtifactCard`. */
  working?: React.ReactNode;
  onRename?: (next: string) => void;
  theme?: HeaderTheme;
  forceHover?: boolean;
  onClick?: () => void;
  onMoreClick?: () => void;
}

/**
 * One Directory item, wired for drag-to-reorder.
 *
 * A wrapper and not a prop on the item itself, because `useSortable` is a hook and a hook cannot be
 * called in the panel's `map`. It is also the seam that keeps `DirectoryRow` and `ImageCard`
 * library-agnostic: they take a plain ref, a plain props bag and two booleans, so their own stories
 * still render them with no `DndContext` anywhere in the tree.
 *
 * The two refs are the point of this file. `setNodeRef` goes on the CELL — that is the box dnd-kit
 * measures and translates, and it has to be the whole item or the gap it opens is the wrong size.
 * `setActivatorNodeRef` plus the listeners go on the BUTTON inside it, which is one element
 * narrower: the `⋯` is a sibling of that button rather than a child, so pressing the menu cannot
 * start a drag and needs no `stopPropagation` to say so. Rows and cards both split this way since
 * the card grew its own `⋯`.
 *
 * No stylesheet beside it: this component renders none of its own DOM. Everything it decides is
 * either a runtime value handed down as `style` or a boolean the item's OWN stylesheet answers.
 */
export function SortableDirectoryItem({
  item,
  kind,
  artifactLayout = 'grid',
  selected = false,
  menuOpen = false,
  editing = false,
  onEditingChange,
  onRename,
  theme: themeProp,
  forceHover = false,
  onClick,
  onMoreClick,
  working,
}: SortableDirectoryItemProps) {
  const theme = useV15Theme(themeProp);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    /*
     * The curve, handed to dnd-kit rather than written in the stylesheet.
     *
     * This looks like the long way round and it is the only correct one. dnd-kit owns WHEN a
     * transition may run — it publishes one while items are shuffling and `null` at the instant the
     * array is reordered, precisely so the swap itself does not animate. A `transition` in CSS
     * cannot be turned off from here, so it also applies at that instant: the row's transform
     * animates from its displaced offset back to zero while the DOM has ALREADY re-indexed it, and
     * every neighbour visibly slides after the list has finished reordering. That is the "order
     * jumps" — and it is invisible in code review, because the stylesheet looks perfectly sensible.
     */
    transition: {
      duration: 200,
      // `--hv15-ease-in-out`, restated because dnd-kit builds a string and cannot read a custom
      // property. A neighbour is travelling between two known points, not entering or leaving.
      easing: 'cubic-bezier(0.77, 0, 0.175, 1)',
    },
  });

  const drag = {
    ref: setNodeRef,
    handleRef: setActivatorNodeRef,
    handleProps: { ...attributes, ...listeners } as React.HTMLAttributes<HTMLElement>,
    style: {
      /*
       * The DRAGGED item takes no transform at all — the overlay is the thing that moves.
       *
       * dnd-kit hands the active item a translation equal to the drag delta, which is what you want
       * when there is no overlay and the real element follows the pointer. With one, applying it
       * means two objects tracking the cursor: the lifted copy, and this box sliding down the list
       * underneath it. Every neighbour still takes its own displacement, which is what opens the
       * gap.
       *
       * `CSS.Transform.toString` produces a real `transform` string, so this composites on the GPU
       * rather than running through a main-thread rAF loop the way a shorthand `x`/`y` prop would.
       */
      transform: isDragging ? undefined : CSS.Transform.toString(transform),
      transition: transition ?? undefined,
    } as React.CSSProperties,
    dragging: isDragging,
  };

  if (kind === 'artifacts' && artifactLayout === 'grid') {
    return (
      <ArtifactCard
        item={item}
        selected={selected}
        menuOpen={menuOpen}
        editing={editing}
        onEditingChange={onEditingChange}
        onRename={onRename}
        theme={theme}
        forceHover={forceHover}
        onClick={onClick}
        onMoreClick={onMoreClick}
        working={working}
        drag={drag}
      />
    );
  }

  if (kind === 'images') {
    return (
      <ImageCard
        item={item}
        selected={selected}
        menuOpen={menuOpen}
        editing={editing}
        onEditingChange={onEditingChange}
        onRename={onRename}
        theme={theme}
        forceHover={forceHover}
        onClick={onClick}
        onMoreClick={onMoreClick}
        drag={drag}
      />
    );
  }

  return (
    <DirectoryRow
      item={item}
      kind={kind}
      selected={selected}
      menuOpen={menuOpen}
      editing={editing}
      onEditingChange={onEditingChange}
      onRename={onRename}
      theme={theme}
      forceHover={forceHover}
      onClick={onClick}
      onMoreClick={onMoreClick}
      drag={drag}
    />
  );
}
